import { publish, subscribe } from '../utils/pubsub';
import { ComponentEvents } from './events/ComponentEvents';

/**
 * ComponentNotifier
 *
 * Allows engines and services to notify UI components of state changes
 * without direct coupling to React state or hooks.
 *
 * Usage:
 * - Engine calls notify(name, target, data) to signal a component
 * - Component subscribes to events.onComponentEvent and filters on target
 *
 * This replaces the pattern of publishing on onSystemEvent with a name filter,
 * keeping component-specific signaling separate from system-level events.
 */

class ComponentNotifier {
    /**
     * Notifies a target component of a named event.
     * @param {string} name - event name e.g. 'Repaint', 'LoadingStarted', 'LoadingFinished'
     * @param {string} target - component name e.g. 'YourNotes'
     * @param {any} data - optional payload
     */
    notify = (name, target, data = null) => {
        publish(ComponentEvents.channel, { name, target, data });
    };

    /**
     * Subscribes to component events for a specific engine.
     * Filters on name so caller only receives its own events.
     * @param {string} name - engine name e.g. EngineNames.TemplateEngine
     * @param {Function} fn - callback receives the full event { name, target, data }
     * @returns {Function} unsubscribe function
     */
    subscribe = (name, fn) => {
        return subscribe(ComponentEvents.channel, (e) => {
            if (e.name !== name) return;
            fn(e);
        });
    };
}

const componentNotifier = new ComponentNotifier();
export default componentNotifier;
